import {
  Component,
  Inject,
  Input,
  forwardRef,
  ElementRef,
  EventEmitter,
  ViewChild,
  OnInit,
  ChangeDetectionStrategy,
  Output,
  AfterContentChecked,
  ChangeDetectorRef
} from '@angular/core';
import { NgAisInstantSearch } from 'angular-instantsearch';
import { connectRange } from 'instantsearch.js/es/connectors';
import { CatalogWidgetDirective } from '../catalog-widget';
import { FiltersService } from '@app/shared/components/catalog/services/filters.service';
import { ViewportScroller } from '@angular/common';
import { Logger } from '@app/core/logging';
import { LocaleService } from '@app/core/i18n';

@Component({
  selector: 'vc-catalog-widget-price-range',
  templateUrl: './catalog-widget-price-range.component.html',
  styleUrls: ['./catalog-widget-price-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogWidgetPriceRangeComponent extends CatalogWidgetDirective implements OnInit, AfterContentChecked {
  @Input()
  public precision: number;

  @Input()
  public applyPriceFilter: Event;

  @Output()
  clickFilter = new EventEmitter<any>();

  @ViewChild('priceMin', { read: ElementRef, static: false })
  public priceMinInput: ElementRef;

  @ViewChild('priceMax', { read: ElementRef, static: false })
  public priceMaxInput: ElementRef;

  state: {
    refine: Function;
    range: { min: number; max: number };
    start: [number, number];
    formatter: { from: (number) => string; to: (number) => string };
    widgetParams: {};
  };

  constructor(
    @Inject(forwardRef(() => NgAisInstantSearch)) public instantSearchParent,
    private _viewportScroller: ViewportScroller,
    private _filtersService: FiltersService,
    private _changeDetection: ChangeDetectorRef,
    private logger: Logger,
    private localeService: LocaleService
  ) {
    super('CatalogWidgetPriceRangeComponent');
  }

  public ngOnInit() {
    this.createWidget(connectRange, {
      attributeName: this.options.facet,
      precision: this.precision || 0
    });
    this._filtersService.getPriceFilterUpdateNeeded().subscribe(
      _ =>
        this.updatePriceRange(null, [this.priceMinInput.nativeElement.value, this.priceMaxInput.nativeElement.value]),
      err => this.logger.error('[CATALOG] WidgetPriceRange getPriceFilterUpdateNeeded: ' + err)
    );

    this._filtersService.getPriceFilterStatut().subscribe(
      ({ resetMin, resetMax }) => {
        if (resetMin) {
          this.priceMinInput.nativeElement.value = null;
        }
        if (resetMax) {
          this.priceMaxInput.nativeElement.value = null;
        }
      },
      err => {
        this.logger.error('[CATALOG] WidgetPriceRange getPriceFilterStatut: ' + err);
      }
    );
    super.ngOnInit();
  }

  ngAfterContentChecked() {
    if (this.state.range) {
      this._changeDetection.markForCheck();
    }
  }

  updatePriceRange(event, priceRange) {
    if (!this.state.range) {
      return;
    }
    if (event) {
      event.preventDefault();
      this._viewportScroller.scrollToPosition([0, 0]);
    }
    const min = this.getMin(this._convertPriceInCents(priceRange[0]));
    const max = this.getMax(this._convertPriceInCents(priceRange[1]));
    this.emitTracking(priceRange[0], priceRange[1]);
    this.state.refine([min, max]);
  }

  getMin(min): number {
    if (min) {
      if (min > this.state.range.min) {
        return min;
      }
    }
    return this.state.range.min;
  }

  getMax(max): number {
    if (max && max >= this.state.range.min && max < this.state.range.max) {
      return max;
    }
    return this.state.range.max;
  }

  private getNumber(price: string | number): number {
    return typeof price === 'number' ? price : parseInt(price, 10);
  }

  public convertPricefromCents(priceInCents: string | number): number {
    return this.getNumber(priceInCents) / 100;
  }

  private _convertPriceInCents(price: string | number): number {
    return this.getNumber(price) * 100;
  }

  emitTracking(priceMin, priceMax) {
    if (!priceMin && !priceMax) {
      return;
    }
    const item = {
      min: priceMin,
      max: priceMax
    };
    this.clickFilter.emit({ filterItem: item, filterOptions: this.options });
  }

  formatCurrency(amount: number, currency: string): string {
    return this.localeService.formatCurrency(amount, currency);
  }
}
